import { Link } from "react-router-dom";

const SERVICES = [
  {
    title: "Tree Removal",
    slug: "tree-removal",
    tag: "Most Requested",
    tagColor: "#e8632a",
    description: "Safe removal of any tree — dead, diseased, storm-damaged, or hazardous. From ornamentals to 100-foot oaks, we handle every job with precision rigging and full cleanup.",
    bullets: [
      "Any size tree, any situation",
      "Crane service available",
      "Emergency 24/7 response",
      "Complete debris haul-away",
    ],
    price: "From $350",
    cta: "Get Removal Quote",
    image: "https://public.readdy.ai/ai/img_res/96a0efae71574b9db865c86477bc32cd.jpg",
    bg: "bg-white",
  },
  {
    title: "Tree Trimming & Pruning",
    slug: "tree-trimming",
    tag: "ISA Certified",
    tagColor: "#1a3a2e",
    description: "Improve tree health, reduce storm risk, and restore beauty with professional pruning by ISA Certified Arborists. Crown thinning, deadwood removal, clearance cuts — done right.",
    bullets: [
      "ISA Certified Arborists",
      "Crown thinning & cleaning",
      "Deadwood & hazard removal",
      "Storm-prep pruning",
    ],
    price: "From $175",
    cta: "Get Trimming Quote",
    image: "https://readdy.ai/api/search-image?query=professional%20ISA%20certified%20arborist%20in%20safety%20harness%20climbing%20rope%20pruning%20large%20shade%20tree%20canopy%20residential%20yard%20northern%20virginia%20sunny%20day%20pole%20saw%20bucket%20truck%20visible&width=720&height=560&seq=svc2026b&orientation=landscape",
    bg: "bg-[#f8f6f3]",
  },
  {
    title: "Stump Grinding",
    slug: "stump-grinding",
    tag: "Quick & Clean",
    tagColor: "#e8632a",
    description: "Ugly stumps gone — permanently. Our commercial grinders remove stumps 12+ inches below grade, eliminating tripping hazards and freeing your yard for lawn, garden, or construction.",
    bullets: [
      "Ground 12\"+ below surface",
      "Multiple-stump discounts",
      "Wood chips included as mulch",
      "Same-day service available",
    ],
    price: "From $150",
    cta: "Get Grinding Quote",
    image: "https://readdy.ai/api/search-image?query=powerful%20commercial%20stump%20grinder%20machine%20in%20action%20wood%20chips%20flying%20operator%20safety%20gear%20removing%20large%20oak%20stump%20clean%20residential%20backyard%20northern%20virginia%20dramatic%20action&width=720&height=560&seq=svc2026c&orientation=landscape",
    bg: "bg-white",
  },
];

export default function ServicesSection() {
  return (
    <section className="py-24">
      <div className="max-w-7xl mx-auto px-4 lg:px-8">
        {/* Header */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-end mb-16">
          <div>
            <span className="text-[#e8632a] font-bold text-sm uppercase tracking-[3px] block mb-4">Our Services</span>
            <h2 className="text-5xl font-black text-[#1e1e1e] leading-tight">
              Expert Tree Care<br />
              <span className="text-[#1a3a2e]">For Every Situation</span>
            </h2>
          </div>
          <div className="lg:text-right">
            <p className="text-gray-500 text-base leading-relaxed max-w-md ml-auto">
              From towering hazard oaks to stubborn old stumps, our ISA Certified crew handles every job with the right equipment and zero shortcuts.
            </p>
            <Link
              to="/services"
              className="inline-flex items-center gap-2 text-[#e8632a] font-bold uppercase tracking-wide text-sm mt-4 hover:underline cursor-pointer"
            >
              View All Services <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>

        {/* Alternating split-section service blocks */}
        <div className="space-y-6">
          {SERVICES.map((svc, i) => (
            <div
              key={svc.slug}
              className={`${svc.bg} rounded-3xl overflow-hidden`}
            >
              <div className={`grid grid-cols-1 lg:grid-cols-2 ${i % 2 === 1 ? "lg:grid-flow-dense" : ""}`}>
                {/* Image */}
                <div className={`relative h-72 lg:h-auto min-h-[340px] overflow-hidden ${i % 2 === 1 ? "lg:col-start-2" : ""}`}>
                  <img
                    src={svc.image}
                    alt={`${svc.title} service in Northern Virginia`}
                    className="w-full h-full object-cover object-center"
                  />
                  {/* Price badge */}
                  <div className="absolute top-5 left-5 bg-[#e8632a] text-white text-xs font-black uppercase tracking-wider px-4 py-2 rounded-full">
                    {svc.price}
                  </div>
                </div>

                {/* Content */}
                <div className={`p-8 lg:p-12 flex flex-col justify-center ${i % 2 === 1 ? "lg:col-start-1 lg:row-start-1" : ""}`}>
                  {/* Tag */}
                  <div
                    className="inline-flex items-center gap-2 text-white text-xs font-black uppercase tracking-[2px] px-3 py-1.5 rounded-full mb-5 self-start"
                    style={{ backgroundColor: svc.tagColor }}
                  >
                    <i className="ri-check-fill"></i>
                    {svc.tag}
                  </div>

                  <h3 className="text-3xl lg:text-4xl font-black text-[#1e1e1e] mb-4 leading-tight">
                    {svc.title}
                  </h3>
                  <p className="text-gray-500 text-base leading-relaxed mb-6">{svc.description}</p>

                  {/* Bullet features */}
                  <ul className="space-y-2.5 mb-8">
                    {svc.bullets.map((b) => (
                      <li key={b} className="flex items-center gap-3">
                        <div className="w-5 h-5 flex items-center justify-center bg-[#e8632a]/15 rounded-full flex-shrink-0">
                          <i className="ri-check-line text-[#e8632a] text-xs font-bold"></i>
                        </div>
                        <span className="text-gray-700 text-sm font-medium">{b}</span>
                      </li>
                    ))}
                  </ul>

                  {/* CTA row */}
                  <div className="flex flex-wrap items-center gap-4">
                    <Link
                      to="/contact"
                      className="flex items-center gap-2 px-6 py-3 bg-[#e8632a] text-white font-black rounded-xl uppercase tracking-wide hover:bg-[#d0541e] transition-all cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-file-list-3-line"></i>
                      {svc.cta}
                    </Link>
                    <Link
                      to={`/services/${svc.slug}`}
                      className="flex items-center gap-2 font-bold text-[#1a3a2e] hover:text-[#e8632a] transition-colors cursor-pointer text-sm uppercase tracking-wide"
                    >
                      Learn More <i className="ri-arrow-right-line"></i>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Post-services CTA */}
        <div className="mt-12 bg-[#1a3a2e] rounded-3xl p-8 lg:p-10 flex flex-col lg:flex-row items-center justify-between gap-6">
          <div>
            <h3 className="text-white font-black text-2xl mb-2">Need a Service Not Listed?</h3>
            <p className="text-white/60 text-sm">We also handle lot clearing, crown reduction, and hazard tree assessment. Call to discuss your project.</p>
          </div>
          <div className="flex items-center gap-4 flex-shrink-0">
            <a
              href="tel:+15407764488"
              className="flex items-center gap-2 px-6 py-3 bg-[#e8632a] text-white font-black rounded-xl uppercase tracking-wide hover:bg-[#d0541e] transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-phone-fill"></i>
              (540) 776-4488
            </a>
            <Link
              to="/contact"
              className="px-6 py-3 border-2 border-white/30 text-white font-black rounded-xl uppercase tracking-wide hover:border-white transition-all cursor-pointer whitespace-nowrap"
            >
              Free Estimate
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
